const PERIOD = {
  YEAR: 'year',
  QUARTER: 'quarter',
  MONTH: 'month',
  WEEK: 'week',
  DAY: 'day',
};

const PERIOD_TYPE = {
  LAST: 'last',
  THIS: 'this',
  FIXED: 'fixed',
  FIXED_WEEKS: 'fixedWeeks',
};

const COMPARE_PERIOD = {
  PERIOD: 'period',
  YEAR: 'year',
  FIXED: 'fixed',
};

const DEFAULT_FORMAT = 'D MMM YYYY';

function getPeriodFormat(period) {
  switch (period) {
    case PERIOD.YEAR:
      return 'YYYY';
    case PERIOD.QUARTER:
    case PERIOD.MONTH:
      return 'MMMM YYYY';
    case PERIOD.WEEK:
      return 'w GGGG';
    default:
      return `ddd ${DEFAULT_FORMAT}`;
  }
}

function getShortPeriodFormat(period) {
  switch (period) {
    case PERIOD.YEAR:
      return 'YYYY';
    case PERIOD.QUARTER:
    case PERIOD.MONTH:
      return 'MMM YYYY';
    case PERIOD.WEEK:
      return 'w';
    default:
      return DEFAULT_FORMAT;
  }
}

function formatDateRange(dayjs, period, startDate, endDate, {
  week = false,
  weekDates = true,
  short = false,
  timeOffset = 0,
  locale,
  timeZone = 'Europe/Stockholm',
} = {}) {
  const dateFormat = short ? getShortPeriodFormat(period) : getPeriodFormat(period);

  let start = dayjs(startDate).tz(timeZone).subtract(timeOffset, 'days');
  let end = dayjs(endDate).tz(timeZone).subtract(timeOffset, 'days');

  if (locale) {
    start = start.locale(locale);
    end = end.locale(locale);
  }

  const formattedStartDate = start.format(dateFormat);
  const formattedEndDate = end.format(dateFormat);

  let string = formattedStartDate;

  if (formattedStartDate !== formattedEndDate) {
    string = `${string} - ${formattedEndDate}`;
  }

  if (period === PERIOD.WEEK) {
    if (week) {
      string = `${week} ${string}`;
    }

    if (!short && weekDates) {
      string = `${string} (${start.format(DEFAULT_FORMAT)} - ${end.format(DEFAULT_FORMAT)})`;
    }
  }

  return string;
}

function calculateMainDateRange(dayjs, filterDateRange, initialDate) {
  const {
    periodType,
    lastNumberInput,
    period,
    specificDates,
  } = filterDateRange;

  let startDate;
  let endDate;

  if ((periodType === PERIOD_TYPE.FIXED || periodType === PERIOD_TYPE.FIXED_WEEKS) && specificDates) {
    startDate = dayjs(specificDates.startDate);
    endDate = dayjs(specificDates.endDate);
  } else {
    startDate = dayjs(initialDate);
    endDate = dayjs(initialDate);
  }

  // startDate = startDate.locale(locale);
  // endDate = endDate.locale(locale);

  if (periodType === PERIOD_TYPE.LAST && lastNumberInput && period) {
    startDate = startDate.subtract(lastNumberInput, period);
    endDate = endDate.subtract(1, period);
  }

  startDate = startDate.startOf(period);
  endDate = endDate.endOf(period);

  return {
    startDate,
    endDate,
    period,
  };
}

function calculateCompareDateRanges(dayjs, mainDateRange, {
  compareToPrevious, compareToPreviousValue, comparePeriod, period, periodType, lastNumberInput, compareSpecificDates,
}) {
  const comparePeriods = [];

  if (mainDateRange && compareToPrevious && compareToPreviousValue > 0) {
    const { startDate, endDate } = mainDateRange;
    let compareStartDate = dayjs(startDate);
    let compareEndDate = dayjs(endDate);

    for (let i = 0; i < compareToPreviousValue; i += 1) {
      if (comparePeriod === COMPARE_PERIOD.YEAR) {
        const startWeek = compareStartDate.week();
        const endWeek = compareEndDate.week();

        compareStartDate = compareStartDate.year(compareStartDate.year() - 1);
        compareEndDate = compareEndDate.year(compareEndDate.year() - 1);

        if (period === PERIOD.WEEK) {
          compareStartDate = compareStartDate.week(startWeek).weekday(0);
          compareEndDate = compareEndDate.week(endWeek).weekday(6);
        }
      } else if (comparePeriod === COMPARE_PERIOD.FIXED && compareSpecificDates?.length > i) {
        compareStartDate = dayjs(compareSpecificDates[i].startDate);
        compareEndDate = dayjs(compareSpecificDates[i].endDate);
      } else if (periodType === PERIOD_TYPE.FIXED || periodType === PERIOD_TYPE.FIXED_WEEKS) {
        const duration = dayjs.duration(endDate.diff(startDate));
        compareEndDate = dayjs(compareStartDate).subtract(1, period).endOf(period);
        compareStartDate = compareStartDate.subtract(duration);
      } else {
        compareStartDate = compareStartDate.subtract(lastNumberInput, period).startOf(period);
        compareEndDate = compareEndDate.subtract(lastNumberInput, period).endOf(period);
      }

      comparePeriods.push({
        startDate: compareStartDate,
        endDate: compareEndDate,
        period: comparePeriod === COMPARE_PERIOD.FIXED ? PERIOD.DAY : period,
      });
    }
  }
  return comparePeriods;
}

function calculatePeriods(
  dayjs,
  timezone,
  dateRange,
  { period, periodType, lastNumberInput },
) {
  let periodName = PERIOD.DAY;

  let startDate = dayjs(dateRange.startDate);
  let endDate = dayjs(dateRange.endDate);

  if (periodType === PERIOD_TYPE.LAST && lastNumberInput > 2) {
    periodName = period;
  } else if (periodType === PERIOD_TYPE.FIXED_WEEKS) {
    periodName = PERIOD.WEEK;
  } else {
    const days = endDate.diff(startDate, 'days');

    if (days > 365 * 3) {
      periodName = PERIOD.YEAR;
    } else if (days > 90) {
      periodName = PERIOD.MONTH;
    }
  }

  const periods = [];

  startDate = startDate.tz(timezone).startOf(periodName);
  endDate = endDate.tz(timezone).endOf(periodName);

  while (startDate < endDate) {
    periods.push({
      start: startDate.startOf(periodName),
      end: startDate.endOf(periodName),
      period: periodName,
    });
    startDate = startDate.add(1, periodName);
  }

  return periods;
}

function calculateDateRanges(dayjs, filterDateRange, timezone, initialDate) {
  if (!filterDateRange) return [];

  const mainDateRange = calculateMainDateRange(dayjs, filterDateRange, initialDate);
  const dateRanges = [];

  if (mainDateRange) {
    dateRanges.push(mainDateRange, ...calculateCompareDateRanges(dayjs, mainDateRange, filterDateRange));
  }

  if (timezone) {
    for (const dateRange of dateRanges) {
      dateRange.periods = calculatePeriods(dayjs, timezone, dateRange, filterDateRange);
    }
  }

  return dateRanges;
}

function getDefaultDateRange(dateRange = {}) {
  // Generates a DEFAULT dateRange, used for creating new `DateRangeFilter` item och to RESET date range
  const {
    period = PERIOD.DAY,
    periodType = PERIOD_TYPE.LAST,
    lastNumberInput = 30,
    comparePeriod = 'period',
    compareToPrevious = false,
    compareToPreviousValue = 1,
    specificDates,
    compareSpecificDates,
  } = dateRange;
  return {
    period,
    periodType,
    lastNumberInput,
    comparePeriod,
    compareToPrevious,
    compareToPreviousValue,
    specificDates,
    compareSpecificDates,
  };
}

function formatMainDateRange(dayjs, dateRange, currentDate, args) {
  const mainDateRange = calculateMainDateRange(dayjs, dateRange, currentDate);
  return formatDateRange(dayjs, dateRange.period, mainDateRange.startDate, mainDateRange.endDate, args);
}

function isPeriodsComparable(dayjs, dateRange) {
  if (!dateRange.compareToPrevious || dateRange.comparePeriod !== COMPARE_PERIOD.FIXED) {
    return true;
  }

  const [main, ...compareDateRanges] = calculateDateRanges(dayjs, dateRange);

  const mainDiff = main.endDate.diff(main.startDate, 'days');

  return compareDateRanges?.every(dr => dr.period === main.period && dr.endDate.diff(dr.startDate, 'days') === mainDiff);
}

function isDateAfterDateRangeStart(dayjs, dateRange, date) {
  const dateRanges = calculateDateRanges(dayjs, dateRange);
  return dateRanges.some(dr => dr.startDate.isBefore(date));
}

module.exports = {
  PERIOD,
  PERIOD_TYPE,
  COMPARE_PERIOD,
  getPeriodFormat,
  getShortPeriodFormat,
  formatDateRange,
  formatMainDateRange,
  getDefaultDateRange,
  calculateMainDateRange,
  calculateCompareDateRanges,
  calculateDateRanges,
  isPeriodsComparable,
  isDateAfterDateRangeStart,
};
